// @flow @jsx h

/* eslint-disable jsx-a11y/media-has-caption */

import { Component, h } from 'preact'

export type Caption = {
  in: number,
  out: number,
  text: string
}

type Source = {
  type: string,
  src: string
}

type Props = {
  sources: Source[],
  poster: string,
  preload?: 'auto' | 'metadata' | 'none',
  controls?: boolean,
  loop?: boolean,
  className?: string,
  onCanPlay?: Function,
  onEnded?: Function,
  onPlay?: Function,
  onPause?: Function,
  onDurationChange?: Function,
  onTimeUpdate?: Function,
  onCaptionCueEnter?: Function,
  onCaptionCueExit?: Function,
  playing?: boolean,
  muted?: boolean,
  captions?: Array<Caption>,
  style?: Object
}

class Video extends Component<Props> {
  elm: HTMLVideoElement
  track: TextTrack

  componentDidMount (): void {
    const { elm } = this
    const {
      captions,
      playing,
      onCaptionCueEnter,
      onCaptionCueExit
    } = this.props
    elm.addEventListener('canplay', this.handleCanPlay)
    elm.addEventListener('play', this.handlePlay)
    elm.addEventListener('ended', this.handleEnded)
    elm.addEventListener('pause', this.handlePause)
    elm.addEventListener('durationchange', this.handleDurationChange)
    elm.addEventListener('timeupdate', this.handleTimeUpdate)

    if (captions) {
      this.track = elm.addTextTrack('subtitles', 'Norsk')

      try {
        captions.forEach(a =>
          this.track.addCue(
            new window.VTTCue(Number(a.in), Number(a.out), a.text)
          )
        )
      } catch (ex) {
        captions.forEach(a =>
          this.track.addCue(
            new window.TextTrackCue(Number(a.in), Number(a.out), a.text)
          )
        )
      }

      this.track.mode = 'hidden'

      for (let i = 0; i < this.track.cues.length; i++) {
        const cue = this.track.cues[i]

        try {
          cue.onenter = () => {
            if (typeof onCaptionCueEnter !== 'undefined') {
              onCaptionCueEnter(cue)
            }
          }
          cue.onexit = () => {
            if (typeof onCaptionCueExit !== 'undefined') {
              onCaptionCueExit()
            }
          }
        } catch (_) {
          // discard error
        }
      }
    }

    if (playing) {
      elm.play()
    }
  }

  componentDidUpdate (prevProps: Props): void {
    const { playing, muted } = this.props
    // Update pause or play state
    if (prevProps.playing && !playing) {
      this.elm.pause()
    }
    if (!prevProps.playing && playing) {
      this.elm.play()
    }
    // Updated `muted`
    if (prevProps.muted !== muted) {
      this.elm.muted = muted || false
    }
  }

  componentWillUnmount (): void {
    const { elm } = this
    elm.removeEventListener('canplay', this.handleCanPlay)
    elm.removeEventListener('play', this.handlePlay)
    elm.removeEventListener('ended', this.handleEnded)
    elm.removeEventListener('pause', this.handlePause)
    elm.removeEventListener('durationchange', this.handleDurationChange)
    elm.removeEventListener('timeupdate', this.handleTimeUpdate)
  }

  handleCanPlay = (event: Event): void => {
    const { onCanPlay } = this.props
    if (typeof onCanPlay !== 'undefined') {
      onCanPlay(event)
    }
  }

  handlePlay = (event: Event): void => {
    const { onPlay } = this.props
    if (onPlay) {
      onPlay(event)
    }
  }

  handlePause = (event: Event): void => {
    const { onPause } = this.props
    if (onPause) {
      onPause(event)
    }
  }

  handleDurationChange = (event: Event): void => {
    const { onDurationChange } = this.props
    if (onDurationChange) {
      onDurationChange(event)
    }
  }

  handleTimeUpdate = (event: Event): void => {
    const { onTimeUpdate } = this.props
    if (onTimeUpdate) {
      onTimeUpdate(event)
    }
  }

  handleEnded = (event: Event): void => {
    const { onEnded } = this.props
    if (typeof onEnded !== 'undefined') {
      onEnded(event)
    }
  }

  play () {
    this.elm.play()
  }

  updateTime (time: number): void {
    this.elm.currentTime = time
  }

  render () {
    const { controls, loop, sources, poster } = this.props
    const preload = this.props.preload || 'none'
    const muted = this.props.muted || false
    return (
      <video
        controls={controls}
        preload={preload}
        ref={elm => (this.elm = elm)}
        loop={loop}
        muted={muted}
        playsInline
        poster={poster}
        className={this.props.className}
        style={this.props.style}
      >
        {sources.map(source => (
          <source key={source.src} src={source.src} type={source.type} />
        ))}
      </video>
    )
  }
}

export default Video



// WEBPACK FOOTER //
// ./src/lib/@nrk/dh-video-component/index.js